import { IconDeviceFloppy } from '@tabler/icons-react';
import { AxiosError } from 'axios';
import {
    AddSpecialTopicFormFields,
    ChildId,
    CommonFormProps,
    EditSpecialTopicFormFields,
    EmployeeBase,
    EmployeeId,
    EmployeeUserBasicInfo,
    FormModeDependedTexts,
    LessonDTO,
    LessonId,
    SelectOptionType,
    SpecialTopicChildWork,
} from 'types';
import { commonTexts, journalTexts, timetableTexts } from 'consts/text';
import { AppForm, AppFormProps, UnassignedChildrenPicker } from 'components/organisms';
import {
    DatePickerElement,
    FormNavButtons,
    TopicHoursTimePicker,
    TopicNameInputElement,
    SimpleTextAreaElement,
    SimpleCheckboxElement,
    SimpleSelectElement,
} from 'components/molecules';
import { AppFormDetails, AppFormItem, ConditionalRender } from 'components/atoms';
import { LessonPicker } from 'components/organisms/FormItems/LessonPicker/LessonPicker';
import { ChildrenWork } from 'components/organisms/FormItems/ChildrenWork/ChildrenWork';
import { Dayjs } from 'dayjs';
import { useEffect, useMemo, useState } from 'react';
import { useAppQuery } from 'services/reactQuery/useAppQuery';
import StaffHTTPService from 'services/HTTPService/staff/StaffHTTPService';
import { useJournalId } from 'hooks/useJournalId/useJournalId';
import { specialTopicAddEditErrorParser } from 'utils/errorHandlers/specialTopic/specialTopicAddEditErrorParser';
import { formFields } from 'consts/form/formFields';
import { max300LengthRule, max5000LengthRule, min2LengthRule } from 'consts/rules/textRules';
import { useWatch } from 'antd/es/form/Form';
import { parseNotInLessonChildIntoSpecialTopicChildWork } from 'utils/parsers/specialTopic/parseNotInLessonChildIntoSpecialTopicChildWork';
import { parseStringIntoDayjsTime } from 'utils/parsers/dateTime/parseStringIntoDayjsTime';
import { getFullName } from 'utils/getFullName';
import useForm from 'antd/es/form/hooks/useForm';

const addModeTexts: FormModeDependedTexts = {
    name: 'specialTopicAdd',
    title: journalTexts.journalDetails.groupJournal.topics.topicAddFormTitle,
    buttonText: commonTexts.actionLabels.saveChanges,
};

const editModeTexts: FormModeDependedTexts = {
    name: 'specialTopicEdit',
    title: journalTexts.journalDetails.groupJournal.topics.topicEditFormTitle,
    buttonText: commonTexts.actionLabels.saveChanges,
    buttonIcon: <IconDeviceFloppy size={20} />,
};

type SpecialTopicAddEditFormProps<
    FormFieldsType extends AddSpecialTopicFormFields | EditSpecialTopicFormFields,
> = Omit<
    AppFormProps<FormFieldsType>,
    'width' | 'title' | 'name' | 'onFinish' | 'error' | 'withGoBack'
> &
    Pick<CommonFormProps, 'mode'> & {
        axiosError?: AxiosError | null;
        isLoading?: boolean;
        onFinish: (specialTopicData: FormFieldsType) => void;
    } & {
        handleSelectFormTopicDate: (date: Dayjs | null) => void;
        specialLessonsData?: LessonDTO[];
        isSpecialLessonsDataError: boolean;
        isSpecialLessonsDataFetching: boolean;
        specialTopicToEditId?: LessonId;
        specialTopicLessonName?: string;
        specialTopicTeacher?: EmployeeBase | null;
        userEmployeeData?: EmployeeUserBasicInfo;
    };

export const SpecialTopicAddEditForm = <
    FormFieldsType extends AddSpecialTopicFormFields | EditSpecialTopicFormFields,
>({
    mode,
    initialValues,
    axiosError,
    handleSelectFormTopicDate,
    specialLessonsData,
    specialTopicLessonName,
    specialTopicTeacher,
    userEmployeeData,
    isSpecialLessonsDataFetching,
    form,
    ...props
}: SpecialTopicAddEditFormProps<FormFieldsType>) => {
    const [selectedLessonId, setSelectedLessonId] = useState<LessonId | undefined>();
    const [teachersOptions, setTeachersOptions] = useState<SelectOptionType<EmployeeId>[]>([]);
    const [isLessonOwnedByEmployee, setIsLessonOwnedByEmployee] = useState<undefined | boolean>(
        undefined,
    );

    const { journalId } = useJournalId();

    const [formInstance] = useForm<FormFieldsType>(form);

    const withNotInLessonChildrenFormValue = useWatch(
        formFields.withNotInLessonChildren,
        formInstance,
    );
    const notInLessonChildrenFormValues = useWatch<ChildId[] | undefined>(
        formFields.notInLessonChildren,
        formInstance,
    );
    const lessonIdFormValue = useWatch<LessonId | undefined>(formFields.lessonId, formInstance);
    const dateFormValue = useWatch<Dayjs | undefined>(formFields.date, formInstance);

    const isAddMode = mode === 'add';
    const { name, title, buttonText, buttonIcon } = isAddMode ? addModeTexts : editModeTexts;
    const specialTopicAddEditError = axiosError ? specialTopicAddEditErrorParser(axiosError) : '';

    useEffect(() => {
        if (!isAddMode && !selectedLessonId && lessonIdFormValue) {
            setSelectedLessonId(lessonIdFormValue);
        }
    }, [isAddMode, lessonIdFormValue, selectedLessonId]);

    const { data: specialAttendanceData } = useAppQuery(
        'SPECIAL_ATTENDANCE',
        [journalId, selectedLessonId],
        () =>
            StaffHTTPService.specialAttendance.getAttendanceByLessonId(
                journalId,
                selectedLessonId as LessonId,
            ),
        { enabled: !!selectedLessonId },
    );

    const { data: unassignedChildrenData } = useAppQuery(
        'UNASSIGNED_CHILDREN',
        [selectedLessonId],
        () => StaffHTTPService.employeeSchedule.getUnassignedChildren(selectedLessonId as LessonId),
        {
            enabled: false,
        },
    );

    const childrenWork = useMemo(() => {
        const parsedSpecialAttendanceIntoChildrenWork: SpecialTopicChildWork[] | undefined =
            specialAttendanceData?.childrenAttendance?.map((childAttendance) => ({
                id: childAttendance.child.id,
                firstName: childAttendance.child.firstName,
                lastName: childAttendance.child.lastName,
                description: null,
                absentType: childAttendance.type,
            }));

        return mode === 'add'
            ? parsedSpecialAttendanceIntoChildrenWork
            : initialValues?.childrenWork;
    }, [initialValues?.childrenWork, mode, specialAttendanceData?.childrenAttendance]);

    const notInLessonChildrenWork = useMemo(() => {
        if (!unassignedChildrenData || !notInLessonChildrenFormValues) {
            return undefined;
        }
        const selectedNotInLessonChildren = unassignedChildrenData.filter((unassignedChild) =>
            notInLessonChildrenFormValues.includes(unassignedChild.id),
        );
        const parsedNotInLessonChildrenWork: SpecialTopicChildWork[] =
            selectedNotInLessonChildren.map(parseNotInLessonChildIntoSpecialTopicChildWork);
        if (!initialValues?.notInLessonChildrenWork) {
            return parsedNotInLessonChildrenWork;
        }
        const filteredUnassignedInThePastChildrenWork =
            initialValues.notInLessonChildrenWork.filter((unassignedChild) =>
                notInLessonChildrenFormValues.includes(unassignedChild.id),
            );
        return filteredUnassignedInThePastChildrenWork.concat(parsedNotInLessonChildrenWork);
    }, [
        initialValues?.notInLessonChildrenWork,
        notInLessonChildrenFormValues,
        unassignedChildrenData,
    ]);

    const cleanupAllLessonDependencies = () => {
        setSelectedLessonId(undefined);
        setTeachersOptions([]);
        formInstance?.resetFields([
            'timeFrom',
            'timeTo',
            'lessonId',
            'teacherId',
            formFields.notInLessonChildren,
            formFields.notInLessonChildrenWork,
        ]);
    };

    const addLessonTeachersOptions = (teachers: EmployeeBase[]) => {
        const lessonTeachersOptions = teachers.map((teacher) => ({
            value: teacher.id,
            label: getFullName(teacher.firstName, teacher.lastName),
        }));

        setTeachersOptions(lessonTeachersOptions);
    };

    const populateLessonDependentData = (lesson: LessonDTO) => {
        // @ts-ignore
        formInstance.setFieldsValue({
            timeFrom: parseStringIntoDayjsTime(lesson.timeFrom),
            timeTo: parseStringIntoDayjsTime(lesson.timeTo),
        });

        if (lesson.teachers?.length && lesson.teachers?.length > 1) {
            if (userEmployeeData) {
                const userTeacherData = lesson.teachers.find(
                    (teacher) => teacher.id === userEmployeeData.id,
                );
                if (userTeacherData) {
                    formInstance.setFieldValue(formFields.teacherId, userTeacherData.id);
                    setIsLessonOwnedByEmployee(true);
                }
            }
        } else if (lesson.teachers?.length === 1) {
            formInstance.setFieldValue(formFields.teacherId, lesson.teachers[0].id);
        } else {
            formInstance.setFieldValue(formFields.teacherId, '');
        }

        setSelectedLessonId(lesson.id);

        if (lesson.teachers) {
            addLessonTeachersOptions(lesson.teachers);
        }
    };

    const onLessonChange = (lesson?: LessonDTO) => {
        if (!lesson) {
            cleanupAllLessonDependencies();
            return;
        }

        formInstance?.resetFields([
            formFields.teacherId,
            formFields.notInLessonChildren,
            formFields.notInLessonChildrenWork,
        ]);

        populateLessonDependentData(lesson);
    };

    const onDateChange = (date: Dayjs | null) => {
        formInstance?.resetFields([
            'timeFrom',
            'timeTo',
            'lessonId',
            'teacherId',
            formFields.notInLessonChildren,
            formFields.notInLessonChildrenWork,
        ]);
        setSelectedLessonId(undefined);
        handleSelectFormTopicDate(date);
    };

    const withNotInLessonChildren = !!withNotInLessonChildrenFormValue;

    return (
        <AppForm<FormFieldsType>
            maxWidth={570}
            name={name}
            title={title}
            error={specialTopicAddEditError}
            withGoBack
            form={formInstance}
            {...props}
        >
            <TopicNameInputElement
                optional
                hasOptionalText
                rules={[min2LengthRule, max300LengthRule]}
            />
            <DatePickerElement isHalfParentWidth disabled={!isAddMode} onChange={onDateChange} />
            <LessonPicker
                specialLessonsData={specialLessonsData}
                isLoading={isSpecialLessonsDataFetching}
                disabled={!dateFormValue}
                hidden={!isAddMode}
                onChange={onLessonChange}
            />
            <ConditionalRender condition={!isAddMode}>
                <AppFormDetails
                    label={journalTexts.journalDetails.groupJournal.topics.lessonLabel}
                    value={specialTopicLessonName}
                />
            </ConditionalRender>
            <AppFormItem justifyContent="space-between" doubleItem>
                <TopicHoursTimePicker mode="start" disabled />
                <TopicHoursTimePicker mode="finish" disabled />
            </AppFormItem>
            <SimpleTextAreaElement
                name="description"
                label={timetableTexts.lessons.lessonsDescription}
                optional
                hasOptionalText
                rules={[max5000LengthRule]}
                rows={6}
            />
            <ConditionalRender condition={isAddMode}>
                <SimpleSelectElement
                    name={formFields.teacherId}
                    label={commonTexts.dataLabels.teacher}
                    disabled={
                        !lessonIdFormValue ||
                        isLessonOwnedByEmployee ||
                        teachersOptions.length === 1
                    }
                    allowClear={false}
                    options={teachersOptions}
                />
            </ConditionalRender>
            <ConditionalRender condition={!isAddMode}>
                <AppFormDetails
                    label={commonTexts.dataLabels.teacher}
                    value={
                        specialTopicTeacher
                            ? getFullName(
                                  specialTopicTeacher.firstName,
                                  specialTopicTeacher.lastName,
                              )
                            : ''
                    }
                />
            </ConditionalRender>
            <ChildrenWork childrenWork={childrenWork} />
            <SimpleCheckboxElement
                name={formFields.withNotInLessonChildren}
                label={journalTexts.journalDetails.specialJournal.topic.selectChildOutOfLesson}
                optional
            />
            <ConditionalRender condition={withNotInLessonChildren}>
                <UnassignedChildrenPicker
                    lessonId={selectedLessonId}
                    selectedUnassignedInThePastChildren={initialValues?.notInLessonChildrenWork}
                />
            </ConditionalRender>
            <ConditionalRender condition={withNotInLessonChildren}>
                <SimpleTextAreaElement
                    name="notInLessonDescription"
                    label={timetableTexts.lessons.lessonsDescription}
                    optional
                    hasOptionalText
                    rules={[max5000LengthRule]}
                    rows={6}
                />
            </ConditionalRender>
            <ChildrenWork
                name={formFields.notInLessonChildrenWork}
                childrenWork={notInLessonChildrenWork}
            />
            <FormNavButtons
                formValuesBeforeChanges={initialValues}
                buttonText={buttonText}
                leftIcon={buttonIcon}
                withPlus={isAddMode}
            />
        </AppForm>
    );
};
